// Phase 45.1 — Inline HTML artifact renderer for chat.
//
// Wraps a chat-agent-emitted artifact in a sandboxed iframe with a small
// toolbar (title · open-fullscreen · copy-url · per-export buttons). The
// artifact lives in the same origin's iframe but its `sandbox` attribute
// withholds `allow-same-origin`, so it runs as an opaque origin: it cannot
// read parent cookies/DOM/localStorage, cannot make credentialed fetches,
// cannot navigate the top frame.
//
// Communication with the artifact is one-way (artifact → parent) via
// window.postMessage. We accept events of shape:
//   { type: 'export', kind: 'sql'|'json'|'prompt'|'markdown'|'csv'|'patch',
//     payload: string }
//   { type: 'resize', height: number }
//   { type: 'chat-emit', text: string }      (artifact wants to reply as the user)
//
// The parent never sends messages INTO the iframe — keeps the trust
// boundary one-way which matches the threat model.

import { useEffect, useMemo, useRef, useState, type CSSProperties } from 'react';

export interface ArtifactExport {
  kind: 'sql' | 'json' | 'prompt' | 'markdown' | 'csv' | 'patch';
  label: string;
}

export interface ArtifactDescriptor {
  url: string;
  title: string;
  exports?: ArtifactExport[];
  route_hint?: 'compare' | 'tune' | 'kanban' | 'flow' | 'table' | 'editor';
}

export interface HtmlArtifactProps {
  artifact: ArtifactDescriptor;
  /**
   * Called when the artifact posts an export. Default behavior is to write
   * the payload to the clipboard; the host can override (e.g. to route a
   * 'prompt' export into the chat input).
   */
  onExport?: (event: { kind: ArtifactExport['kind']; payload: string }) => void;
  /**
   * Called when the artifact emits a chat-emit event — used by editor-style
   * artifacts (reclassify-sheet, dismiss-landmine) to push a follow-up
   * message into the chat as if the user typed it.
   */
  onChatEmit?: (text: string) => void;
  /** Default-collapsed on long conversations. */
  defaultCollapsed?: boolean;
  /** Apex height when not collapsed; iframe auto-resizes up to this cap. */
  maxHeightPx?: number;
}

const FRAME_BG = '#0d1116';
const TOOLBAR_BG = '#1a212b';
const BORDER = '#252e3a';
const FG = '#d6deea';
const ACCENT = '#3ea0ff';

const wrapStyle: CSSProperties = {
  border: `1px solid ${BORDER}`,
  borderRadius: 6,
  overflow: 'hidden',
  background: FRAME_BG,
  marginBottom: 12,
};
const toolbarStyle: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: 8,
  padding: '8px 10px',
  background: TOOLBAR_BG,
  borderBottom: `1px solid ${BORDER}`,
  fontSize: 12,
  color: FG,
};
const titleStyle: CSSProperties = {
  flex: 1,
  whiteSpace: 'nowrap',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  fontWeight: 600,
};
const buttonStyle: CSSProperties = {
  border: `1px solid ${BORDER}`,
  background: '#0d1116',
  color: FG,
  padding: '4px 8px',
  borderRadius: 4,
  cursor: 'pointer',
  fontSize: 11,
  fontFamily: 'inherit',
};
const primaryButtonStyle: CSSProperties = {
  ...buttonStyle,
  color: ACCENT,
  borderColor: ACCENT,
};
const iframeStyle: CSSProperties = {
  display: 'block',
  width: '100%',
  height: 480,
  border: 'none',
  background: '#0d1116',
};

export function HtmlArtifact({
  artifact,
  onExport,
  onChatEmit,
  defaultCollapsed = false,
  maxHeightPx = 720,
}: HtmlArtifactProps) {
  const [collapsed, setCollapsed] = useState(defaultCollapsed);
  const [height, setHeight] = useState<number>(480);
  const [copied, setCopied] = useState<string | null>(null);
  const frameRef = useRef<HTMLIFrameElement | null>(null);
  const apiBase = useMemo(() => {
    return import.meta.env.DEV ? '/api/v1' : 'https://ultimate-bidder-api.frankpedersen84.workers.dev/api/v1';
  }, []);
  const fullUrl = `${apiBase}${artifact.url.replace(/^\/api\/v1/, '')}`;

  useEffect(() => {
    function onMessage(ev: MessageEvent) {
      // Frame is origin-opaque (sandbox without allow-same-origin), so
      // ev.origin will be "null". Verify the source matches our iframe.
      if (!frameRef.current || ev.source !== frameRef.current.contentWindow) return;
      const data = ev.data as { type?: string; kind?: string; payload?: string; height?: number; text?: string };
      if (!data || typeof data !== 'object') return;

      if (data.type === 'export' && typeof data.kind === 'string' && typeof data.payload === 'string') {
        const exp = { kind: data.kind as ArtifactExport['kind'], payload: data.payload };
        if (onExport) {
          onExport(exp);
        } else {
          // Default: clipboard.
          if (navigator.clipboard?.writeText) {
            navigator.clipboard.writeText(data.payload).catch(() => undefined);
          }
        }
        setCopied(data.kind);
        setTimeout(() => setCopied(null), 2000);
        return;
      }
      if (data.type === 'resize' && typeof data.height === 'number') {
        setHeight(Math.min(maxHeightPx, Math.max(120, Math.round(data.height))));
        return;
      }
      if (data.type === 'chat-emit' && typeof data.text === 'string' && onChatEmit) {
        onChatEmit(data.text);
        return;
      }
    }
    window.addEventListener('message', onMessage);
    return () => window.removeEventListener('message', onMessage);
  }, [maxHeightPx, onChatEmit, onExport]);

  const onOpenFullscreen = () => {
    window.open(fullUrl, '_blank', 'noopener,noreferrer');
  };
  const onCopyUrl = () => {
    if (navigator.clipboard?.writeText) {
      navigator.clipboard.writeText(window.location.origin + fullUrl).catch(() => undefined);
      setCopied('url');
      setTimeout(() => setCopied(null), 2000);
    }
  };

  return (
    <div style={wrapStyle}>
      <div style={toolbarStyle}>
        <span style={{ color: '#7a8593' }} aria-hidden="true">▦</span>
        <span style={titleStyle}>{artifact.title || 'Artifact'}</span>
        <button type="button" style={buttonStyle} onClick={() => setCollapsed((c) => !c)} aria-label={collapsed ? 'Expand artifact' : 'Collapse artifact'}>
          {collapsed ? 'Expand' : 'Collapse'}
        </button>
        <button type="button" style={buttonStyle} onClick={onCopyUrl}>
          {copied === 'url' ? 'Copied' : 'Copy URL'}
        </button>
        <button type="button" style={primaryButtonStyle} onClick={onOpenFullscreen}>
          Open
        </button>
      </div>
      {!collapsed && (
        <iframe
          ref={frameRef}
          title={artifact.title || 'Chat artifact'}
          src={fullUrl}
          // allow-scripts only. NO allow-same-origin — origin-isolated.
          sandbox="allow-scripts"
          loading="lazy"
          referrerPolicy="no-referrer"
          style={{ ...iframeStyle, height }}
        />
      )}
      {!collapsed && Array.isArray(artifact.exports) && artifact.exports.length > 0 && (
        <div style={{ padding: '6px 10px', borderTop: `1px solid ${BORDER}`, color: '#7a8593', fontSize: 11 }}>
          The artifact can export: {artifact.exports.map((e) => e.label).join(' · ')}
        </div>
      )}
    </div>
  );
}
